import { Contact, ContactType } from "@repo/server-api";
import { MenuKeys, TabKeys, RuleFactoryType } from "@repo/server-api/types";

export const RuleFactory: RuleFactoryType = {
	isFreelancer: (contact: Contact | null) => () =>
		contact?.contactTypes ? contact.contactTypes.includes(ContactType.Freelancer) : false,
	isCompany: (contact: Contact | null) => () => !!contact?.isACompany,
	isPerson: (contact: Contact | null) => () => !!contact?.isAPerson,
	isContractor: (contact: Contact | null) => () =>
		contact?.contactTypes ? contact.contactTypes.includes(ContactType.Contractor) : false,
	isEmployee: (contact: Contact | null) => () =>
		contact?.contactTypes ? contact.contactTypes.includes(ContactType.Employee) : false,
	showTypeSection: (contact: Contact | null) => () => {
		return contact?.contactTypes
			? (contact.contactTypes.includes(ContactType.Freelancer) ||
					contact.contactTypes.includes(ContactType.Company) ||
					contact.contactTypes.includes(ContactType.Client) ||
					contact.contactTypes.includes(ContactType.Supplier)) &&
					!contact.contactTypes.includes(ContactType.Contractor)
			: false;
	},
	isClient: (contact: Contact | null) => () =>
		contact?.contactTypes ? contact.contactTypes.includes(ContactType.Client) : false,
	hasSpecificTypes: (contact: Contact | null, types: ContactType[]) => () =>
		types.every((type) => (contact?.contactTypes ? contact.contactTypes.includes(type) : false)),
	generalSaveButton: (tab: TabKeys) => () =>
		tab !== TabKeys.MEMBERS &&
		tab !== TabKeys.CONTACT_PERSONS &&
		tab !== TabKeys.LOGIN &&
		tab !== TabKeys.BILLING_INFORMATION &&
		tab !== TabKeys.BCC_LISTS &&
		tab !== TabKeys.DEPARTMENTS,
	loginSaveButton: (tab: TabKeys) => () => tab === TabKeys.LOGIN,
	allowedTabsForContact: (contact: Contact | null) => {
		const keys: TabKeys[] = [TabKeys.GENERAL_INFORMATION];
		contact?.contactTypes?.forEach((type) => {
			switch (type) {
				case ContactType.Person:
					keys.push(TabKeys.LOGIN);
					break;
				case ContactType.Company:
					keys.push(TabKeys.CONTACT_PERSONS);
					keys.push(TabKeys.BILLING_INFORMATION);
					break;
				case ContactType.Client:
					break;
				case ContactType.Supplier:
					keys.push(TabKeys.MEMBERS);
					keys.push(TabKeys.PAYMENT_INFORMATION);
					break;
				case ContactType.Employee:
					keys.push(TabKeys.WORKING_CONDITIONS);
					keys.push(TabKeys.SALARY_INFORMATION);
					break;
				case ContactType.Freelancer:
					keys.push(TabKeys.SALARY_INFORMATION);
					break;
				case ContactType.Contractor:
					keys.push(TabKeys.CONTRACTED);
					keys.push(TabKeys.MEMBERS);
					keys.push(TabKeys.ROLLING_NUMBERS);
					keys.push(TabKeys.BCC_LISTS);
					keys.push(TabKeys.DEPARTMENTS);
					keys.push(TabKeys.DUNNINGS);
					break;
				case ContactType.ContactPerson:
					break;
				default:
					break;
			}
		});

		return keys;
	},
	allowedMenuItem(itemKey: MenuKeys, useUserRoles: string[]) {
		const allowedItems: Record<MenuKeys, string[]> = {
			// Main Features
			[MenuKeys.MAIN_FEATURES]: [
				"contractor_hr",
				"tenant_hr",
				"tenant_administrator",
				"contractor_administrator",
				"project_manager",
				"employee",
				"freelancer",
				"freelancer_of_supplier",
			],
			[MenuKeys.DASHBOARD]: [
				"contractor_hr",
				"tenant_hr",
				"tenant_administrator",
				"contractor_administrator",
				"project_manager",
				"employee",
				"freelancer",
				"freelancer_of_supplier",
			],
			[MenuKeys.CONTRACTORS]: [
				"contractor_hr",
				"tenant_hr",
				"tenant_administrator",
				"contractor_administrator",
				"project_manager",
			],
			[MenuKeys.EMPLOYEES]: [
				"contractor_hr",
				"tenant_hr",
				"tenant_administrator",
				"contractor_administrator",
				"project_manager",
			],
			[MenuKeys.FREELANCERS]: [
				"contractor_hr",
				"tenant_hr",
				"tenant_administrator",
				"contractor_administrator",
				"project_manager",
			],
			[MenuKeys.CLIENTS]: [
				"contractor_hr",
				"tenant_hr",
				"tenant_administrator",
				"contractor_administrator",
				"project_manager",
			],
			[MenuKeys.SUPPLIERS]: [
				"contractor_hr",
				"tenant_hr",
				"tenant_administrator",
				"contractor_administrator",
				"project_manager",
			],
			// Projects
			[MenuKeys.PROJECTS]: ["project_manager", "freelancer", "employee", "freelancer_of_supplier"],
			// Sub-item for Projects
			[MenuKeys.PROJECTS_LIST]: ["project_manager"],
			[MenuKeys.PROJECTS_TIMESHEETS]: ["project_manager"],
			[MenuKeys.PROJECTS_MONTHLY_OVERVIEW]: ["project_manager"],

			// Admin
			[MenuKeys.ADMIN]: ["tenant_administrator", "contractor_administrator"],
			// Admin sub-items
			[MenuKeys.ADMIN_LOGINS]: ["tenant_administrator", "contractor_administrator"],

			// Supplier Impersonation
			[MenuKeys.SUPPLIER_IMPERSONATION]: ["freelancer"],

			// Settings
			[MenuKeys.SETTINGS]: [
				"tenant_administrator",
				"contractor_administrator",
				"project_manager",
				"employee",
				"freelancer",
				"freelancer_of_supplier",
			],
			// Settings sub-items
			[MenuKeys.SETTINGS_ACCOUNT]: ["employee", "freelancer", "freelancer_of_supplier"],
			[MenuKeys.SETTINGS_SERVICES]: ["project_manager"],
			[MenuKeys.SETTINGS_ACCOUNTING_PROFILES]: ["project_manager"],
			[MenuKeys.SETTINGS_DISCOUNT_PROFILES]: ["project_manager"],
			[MenuKeys.SETTINGS_ROLLING_NUMBERS]: ["tenant_administrator", "contractor_administrator"],
			[MenuKeys.SETTINGS_MAIL_CONFIGURATION]: ["tenant_administrator", "contractor_administrator"],

			// Documents generator
			[MenuKeys.DOCUMENT_GENERATOR]: [],
			// Document generator sub-items
			[MenuKeys.DOCUMENT_GENERATOR_TEMPLATES]: [],
			[MenuKeys.DOCUMENT_GENERATOR_INVOICES]: [],

			// Finances
			[MenuKeys.FINANCES]: [],
			// Finances sub-items
			[MenuKeys.FINANCES_DASHBOARD]: [],
			[MenuKeys.FINANCES_INVOICES]: [],

			// Time Management
			[MenuKeys.TIME_MANAGEMENT]: [],
			// Time Management sub-items
			[MenuKeys.CALENDAR]: [],
			[MenuKeys.TIME_BALANCES]: ["contractor_hr", "tenant_hr"],
			[MenuKeys.HR_ABSENCE]: ["contractor_hr", "tenant_hr"],
			[MenuKeys.HR_TIME_ENTRIES]: ["contractor_hr", "tenant_hr"],
			[MenuKeys.TIME_LABELS]: ["contractor_hr", "tenant_hr"],
			[MenuKeys.CALENDAR_EVENTS]: ["contractor_hr", "tenant_hr"],
			[MenuKeys.WORKING_TIME_MODELS]: ["contractor_hr", "tenant_hr"],
			[MenuKeys.ABSENCE_REASONS]: ["contractor_hr", "tenant_hr"],
			[MenuKeys.TIME_ENTRIES]: ["teamleader"],
			[MenuKeys.TEAM_ABSENCE]: ["teamleader"],
			[MenuKeys.MY_TIME_ENTRIES]: ["employee"],
			[MenuKeys.TIMESHEET]: [],
			[MenuKeys.ABSENCE]: ["employee"],
			[MenuKeys.VACATION_REQUESTS]: ["employee"],
			[MenuKeys.MY_TIME_MANAGEMENT]: [],
			[MenuKeys.TEAM_TIME_MANAGEMENT]: [],
			[MenuKeys.TIME_MANAGEMENT_SETTINGS]: [],

			// Accounting TODO
			[MenuKeys.ACCOUNTING]: ["accountant"],
			// Accounting sub-items
			[MenuKeys.ACCOUNTING_ABSENCE_OVERVIEW]: ["accountant"],

			// My Profile
			[MenuKeys.MY_PROJECTS]: ["employee"],
			// My Profile sub-items
			[MenuKeys.MY_PROJECTS_TIME_ENTRIES]: ["employee", "freelancer", "freelancer_of_supplier"],
			[MenuKeys.MY_PROJECTS_TIMESHEETS]: ["employee", "freelancer", "freelancer_of_supplier"],

			// Invoices section
			[MenuKeys.INVOICES]: ["project_manager"],
			// Sub-items for Invoices
			[MenuKeys.INVOICES_DRAFT]: ["project_manager"],
			[MenuKeys.INVOICES_UNSENT]: ["project_manager"],
			[MenuKeys.INVOICES_UNPAID]: ["project_manager"],
			[MenuKeys.INVOICES_OVERDUE]: ["project_manager"],
			[MenuKeys.INVOICES_PAID]: ["project_manager"],
			[MenuKeys.INVOICES_CANCELLED]: ["project_manager"],

			// Offers
			[MenuKeys.OFFERS]: ["project_manager"],
			// Sub-items for Offers
			[MenuKeys.OFFERS_DRAFT]: ["project_manager"],
			[MenuKeys.OFFERS_UNSENT]: ["project_manager"],
			[MenuKeys.OFFERS_OPEN]: ["project_manager"],
			[MenuKeys.OFFERS_ACCEPTED]: ["project_manager"],
			[MenuKeys.OFFERS_REJECTED]: ["project_manager"],
			[MenuKeys.OFFERS_OVERDUE]: ["project_manager"],
		};

		return allowedItems[itemKey]?.some((role) => useUserRoles.includes(role));
	},
	allowedTabsForUser: (_itemKey: TabKeys, useUserRoles: string[]) => {
		const allowedItems: Record<TabKeys, string[]> = {
			[TabKeys.GENERAL_INFORMATION]: [
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
				"tenant_administrator",
				"project_manager",
			],
			[TabKeys.CONTRACTED]: ["tenant_administrator", "tanent_hr"],
			[TabKeys.MEMBERS]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],
			[TabKeys.WORKING_CONDITIONS]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],
			[TabKeys.EXTERNAL_WORKING_CONDITIONS]: [],
			[TabKeys.SALARY_INFORMATION]: [
				"tenant_hr",
				"contractor_hr",
				"tenant_administrator",
				"contractor_administrator",
			],
			[TabKeys.CONTRACTOR_SELECTION]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],
			[TabKeys.PERSON_IDENTIFICATION]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],
			[TabKeys.CONTACT_INFORMATION]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],
			[TabKeys.CONTACT_PERSONS]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],
			[TabKeys.ACTIONS]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],
			[TabKeys.ADDRESS_INFORMATION]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],
			[TabKeys.DEPARTMENT_INFORMATION]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],

			[TabKeys.BILLING_INFORMATION]: ["tenant_administrator", "tenant_hr"],
			[TabKeys.BILLING_ADDRESS]: ["tenant_administrator", "tenant_hr"],
			[TabKeys.SHIPPING_ADDRESS]: ["tenant_administrator", "tenant_hr"],
			[TabKeys.BANK_ACCOUNT]: ["tenant_administrator", "tenant_hr"],
			[TabKeys.PAYMENT_INFORMATION]: ["tenant_administrator", "tenant_hr"],
			[TabKeys.ROLLING_NUMBERS]: ["tenant_administrator", "tenant_hr", "contractor_administrator"],
			[TabKeys.BCC_LISTS]: ["tenant_administrator", "tenant_hr", "contractor_administrator"],
			[TabKeys.DEPARTMENTS]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_administrator",
				"contractor_hr",
			],
			[TabKeys.DUNNINGS]: ["tenant_administrator", "tenant_hr", "contractor_administrator"],

			[TabKeys.LOGIN]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
			],

			[TabKeys.AGREEMENT]: ["project_manager"],
			[TabKeys.ACCOUNTING_INFORMATION]: ["project_manager"],
			[TabKeys.POSITION_INFORMATION]: ["project_manager"],

			[TabKeys.TIME_LABELS]: ["contractor_hr", "tenant_hr"],
			[TabKeys.TIME_GROUPS]: ["contractor_hr", "tenant_hr"],

			[TabKeys.LAYOUT]: [],
			[TabKeys.LOGO]: [],
			[TabKeys.DOCUMENT_SETTINGS]: [],
			[TabKeys.TABLE_SETTINGS]: [],
			[TabKeys.HEADER_FOOTER]: [],
			[TabKeys.CONTENT]: [],
			[TabKeys.HEADER]: [],
			[TabKeys.FOOTER]: [],

			[TabKeys.ABSENCE]: ["employee"],
			[TabKeys.CALENDAR]: ["employee"],
			[TabKeys.VACATION_REQUESTS]: ["employee"],

			[TabKeys.HOURLY_RATE]: ["project_manager"],
			[TabKeys.FIXED_PRICE]: ["project_manager"],

			[TabKeys.TEAM_MEMBERS]: [
				"tenant_administrator",
				"tenant_hr",
				"contractor_hr",
				"contractor_administrator",
				"project_manager",
			],
			[TabKeys.TEMPLATE_INFORMATION]: [],
		};

		return Object.keys(allowedItems)
			.filter((tabKey) =>
				allowedItems[tabKey as TabKeys].some((role) => useUserRoles.includes(role)),
			)
			.map((tabKey) => tabKey as TabKeys);
	},
};
